import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Bell, Flame, Gauge, Zap } from "lucide-react";

import { telemetryApi } from "@/api/telemetry";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number | null, digits = 0): string {
  if (n === null) return "—";
  return n.toLocaleString("uk-UA", { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

export function SessionPage() {
  const { t } = useTranslation();
  const live = useQuery({
    queryKey: ["telemetry-live"],
    queryFn: () => telemetryApi.live(),
    refetchInterval: 15_000,
  });

  const v = live.data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("session.title")}</CardTitle>
              <CardDescription>{t("session.subtitle")}</CardDescription>
            </div>
            {v && (
              <div className="text-xs text-slate-500">
                {t("session.last_update")}:{" "}
                {new Date(v.now).toLocaleTimeString("uk-UA", { timeZone: "Europe/Kyiv" })}
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {!v ? (
        <Card>
          <CardContent className="p-6 text-sm text-slate-500">{t("common.loading")}</CardContent>
        </Card>
      ) : (
        <>
          {v.had_alarm_today && v.plan_submitted_at && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900">
              <div className="font-medium">{t("session.alarm_banner_title")}</div>
              <p className="mt-1">{t("session.alarm_banner_body")}</p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard
              icon={<Zap className="h-5 w-5" />}
              label={t("session.fact_power")}
              value={`${fmt(num(v.fact_power_kw))} ${t("common.kw")}`}
              tone={num(v.fact_power_kw) !== null && num(v.fact_power_kw)! > 0 ? "ok" : undefined}
            />
            <KpiCard
              icon={<Activity className="h-5 w-5" />}
              label={t("session.self_needs")}
              value={`${fmt(num(v.self_needs_kw))} ${t("common.kw")}`}
            />
            <KpiCard
              icon={<Flame className="h-5 w-5" />}
              label={t("session.gas")}
              value={`${fmt(num(v.gas_m3h), 1)} ${t("session.m3h")}`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <StatusCard had_warning={v.had_warning} had_alarm={v.had_alarm} />
            <ImbalanceCard v={v} />
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "ok" | "warn" | "alarm";
}) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div
          className={cn(
            "rounded-full p-2",
            tone === "ok"
              ? "bg-emerald-100 text-emerald-700"
              : tone === "warn"
                ? "bg-amber-100 text-amber-700"
                : tone === "alarm"
                  ? "bg-red-100 text-red-700"
                  : "bg-slate-100 text-slate-600",
          )}
        >
          {icon}
        </div>
        <div className="flex-1">
          <div className="text-xs text-slate-500">{label}</div>
          <div className="text-2xl font-mono font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusCard({
  had_warning,
  had_alarm,
}: {
  had_warning: boolean;
  had_alarm: boolean;
}) {
  const { t } = useTranslation();
  const state = had_alarm ? "alarm" : had_warning ? "warn" : "ok";
  const tone =
    state === "alarm" ? "alarm" : state === "warn" ? "warn" : "ok";
  const label =
    state === "alarm"
      ? t("session.status_alarm")
      : state === "warn"
        ? t("session.status_warning")
        : t("session.status_ok");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {state === "alarm" ? (
            <AlertTriangle className="h-5 w-5 text-red-700" />
          ) : (
            <Bell className="h-5 w-5 text-slate-500" />
          )}
          {t("session.status_card")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "rounded-lg p-4 text-lg font-medium",
            tone === "alarm"
              ? "bg-red-50 text-red-800"
              : tone === "warn"
                ? "bg-amber-50 text-amber-800"
                : "bg-emerald-50 text-emerald-800",
          )}
        >
          {label}
        </div>
      </CardContent>
    </Card>
  );
}

function ImbalanceCard({
  v,
}: {
  v: {
    elapsed_min: number;
    rdn_fact_so_far_kwh: number | string | null;
    rdn_plan_hour_kwh: number | string | null;
    projected_hour_kwh: number | string | null;
    deviation_kwh: number | string | null;
    deviation_pct: number | string | null;
  };
}) {
  const { t } = useTranslation();
  const dev = num(v.deviation_pct);
  const tone = dev === null ? undefined : Math.abs(dev) <= 2 ? "ok" : Math.abs(dev) <= 5 ? "warn" : "alarm";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Gauge className="h-5 w-5 text-slate-500" />
          {t("session.imbalance_card")}
        </CardTitle>
        <CardDescription>{t("session.imbalance_desc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label={t("session.elapsed_min")} value={`${v.elapsed_min} ${t("session.min_short")}`} />
        <Row label={t("session.rdn_fact_so_far")} value={fmt(num(v.rdn_fact_so_far_kwh))} />
        <Row label={t("session.rdn_plan_hour")} value={fmt(num(v.rdn_plan_hour_kwh))} />
        <Row label={t("session.projected_hour")} value={fmt(num(v.projected_hour_kwh))} />
        <Row
          label={t("session.deviation")}
          value={
            dev !== null
              ? `${fmt(num(v.deviation_kwh))} (${fmt(dev, 2)}%)`
              : "—"
          }
          tone={tone}
        />
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "alarm";
}) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1">
      <span className="text-slate-500">{label}</span>
      <span
        className={cn(
          "font-mono",
          tone === "alarm" ? "text-red-700 font-semibold" : tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "",
        )}
      >
        {value}
      </span>
    </div>
  );
}
